import React, {
  memo,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { View, Image, Pressable, Text, StyleSheet } from 'react-native';

import { __WEB__, COLOR, FONT, FONT_SIZE } from '../../../constants/styles';
import { ROLE_TYPE_PATIENT } from '../../../constants/roleTypes.js';

import { I18n } from '../../components';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  getEditingCasePatientEmployerUser,
  getEditingCaseEmployerMedicalServices,
  getEditingCaseEmployerClinicalLocations,
} from '../selector';
import { getGroupsForEmployer, getAssociatedEmployer } from '../../../selectors/employers';
import { addCase, updateCaseEmployer, cancelReadiness } from '../actions';
import { editingCasePermissions } from '../../../helpers/permissions';

import { READINESS_CLEARANCE } from '../../../constants/permissions';

import PersonForm from './person_form.js';

import { SUCCESS } from '../../../actions/statusTypes';

const CaseFieldsAirforce = memo((props) => {
  const formRef = useRef(null);

  useEffect(() => {
    if (props.status === SUCCESS) {
      props.onSave?.();
    }
  }, [props.status]);

  useImperativeHandle(props.forwardedRef, () => ({
    submit,
  }));

  const cancelReadiness = useCallback(() => {
    props.onConfirmationOpts({
      title: I18n.ce('airforce.cancelReadiness'),
      description: I18n.t('airforce.cancelReadinessConfirmMessage'),
      confirm: 'Yes, Cancel',
      reject: 'No',
      onConfirmed: () => {
        props.actions.cancelReadiness(
          props.editingCase.caseId,
          props.editingCaseEmployer.employerId
        );
      },
      onCancelled: () => {},
    });
  }, [props.editingCase, props.editingCaseEmployer]);

  const submit = useCallback(() => {
    const requestObj = formRef.current?.getObjectToSubmit();
    if (!requestObj) {
      return false;
    }
    if (props.editingCase) {
      const patientObj = {
        fullName: requestObj.name,
        email: requestObj.email,
        phone: requestObj.phone,
        role: requestObj.patientRole,
        rank: requestObj.rank,
      };
      const caseObj = {
        group: requestObj.group,
      };
      if (requestObj.employer_department_id)
        patientObj.employer_department_id = requestObj.employer_department_id;
      if (requestObj.dod_id) patientObj.dod_id = requestObj.dod_id;
      if (requestObj.special_status) patientObj.special_status = requestObj.special_status;
      if (requestObj.service_branch) patientObj.service_branch = requestObj.service_branch;

      let employerUserObj;
      const userId = props.editingCasePatient?.userId;
      const employerId = props.editingCaseEmployer?.employerId;
      if (
        userId &&
        props.employerUser &&
        (requestObj.clinicalLocation || requestObj.medicalService)
      ) {
        employerUserObj = {
          clinicalLocation: requestObj.clinicalLocation,
          medicalService: requestObj.medicalService,
          userId,
          employerId,
        };
      }
      props.actions.updateCaseEmployer(props.editingCase.caseId, {
        caseObj,
        patientObj,
        employerUserObj,
      });
    } else {
      const employer = props.currentEmployer;
      const caseObj = {
        ...requestObj,
        description: requestObj.name,
        group: requestObj.group,
        patient: {
          email: requestObj.email,
          name: requestObj.name,
          phone: requestObj.phone,
          rank: requestObj.rank,
          role: requestObj.patientRole,
        },
      };
      if (requestObj.employer_department_id)
        caseObj.patient.employer_department_id = requestObj.employer_department_id;
      if (requestObj.dod_id) caseObj.patient.dod_id = requestObj.dod_id;
      if (requestObj.service_branch) caseObj.patient.service_branch = requestObj.service_branch;
      props.actions.addCase(caseObj, employer.employerId);
    }
    return true;
  });

  const formData = useMemo(() => {
    const patient = props.editingCasePatient;
    if (!patient) return null;
    const employerUser = props.employerUser;
    const kase = props.editingCase;
    return {
      firstName: patient.fullName?.split(' ')[0],
      lastName: patient.fullName?.split(' ').slice(1).join(' '),
      email: patient.email,
      phone: patient.phone,
      dodId: patient.dod_id,
      status: patient.role,
      serviceBranch: patient.service_branch,
      rank: patient.rank,
      group: kase?.group,
      specialStatus: patient.special_status,
      clinicalLocation: employerUser?.clinicalLocation,
      medicalService: employerUser?.medicalService,
      department: patient.departmentId,
      role: employerUser?.role,
    };
  }, [props.editingCasePatient, props.employerUser, props.editingCase]);

  const omittedFields = useMemo(() => {
    const fields = [];
    if (props.editingCase) {
      fields.push('email');
    }
    return fields;
  }, [props.editingCase]);

  return (
    <View style={[{ zIndex: 1000 }]}>
      <View style={[{ zIndex: 1000, paddingHorizontal: 10, paddingTop: 15 }]}>
        <PersonForm
          ref={formRef}
          roleType={ROLE_TYPE_PATIENT}
          formData={formData}
          isVerticalLayout={true}
          medicalServices={props.medicalServices}
          clinicalLocations={props.clinicalLocations}
          groups={props.groups}
          departments={props.departments}
          omittedFields={omittedFields}
        />
      </View>
      {!!props.editingCase && !!props.canEditReadnessClearance && (
        <Pressable style={styles.footerButton} onPress={cancelReadiness}>
          <Image
            style={styles.footerIcon}
            source={require('../../../../resources/icon-logout.png')}
            resizeMode="contain"
          />
          <Text style={styles.footerButtonText}>{I18n.ac('airforce.cancelReadiness')}</Text>
        </Pressable>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  footerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 35,
    paddingVertical: 20,
  },
  footerIcon: {
    tintColor: COLOR.emphasis,
    width: 13,
    height: 15,
    marginRight: 20,
  },
  footerButtonText: {
    ...FONT.semibold,
    fontSize: FONT_SIZE.small,
    color: COLOR.emphasis,
  },
});

function mapStateToProps(state, ownProps) {
  const employer = getAssociatedEmployer(state);
  return {
    status: state.screens.cases.status,
    canEditReadnessClearance: editingCasePermissions(READINESS_CLEARANCE).canEdit,
    employerUser: getEditingCasePatientEmployerUser(state),
    medicalServices:
      getEditingCaseEmployerMedicalServices(state) || employer?.uiConfig?.medicalServices,
    clinicalLocations:
      getEditingCaseEmployerClinicalLocations(state) || employer?.uiConfig?.clinicalLocations,
    groups: getGroupsForEmployer(state),
    departments: ownProps.editingCaseEmployer?.departments || employer?.departments,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        addCase,
        updateCaseEmployer,
        cancelReadiness,
      },
      dispatch
    ),
  };
}

const ForwardedCaseFieldsAirforce = React.forwardRef((props, ref) => (
  <CaseFieldsAirforce {...props} forwardedRef={ref} />
));

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  ForwardedCaseFieldsAirforce
);
